"use client";

import { useState } from "react";
import Link from "next/link";

const V        = "#6768FC";
const V_LIGHT  = "rgba(103,104,252,0.12)";
const V_BORDER = "rgba(103,104,252,0.25)";
const BG       = "#0D0E17";
const SURFACE  = "#1A1B26";
const BORDER   = "rgba(255,255,255,0.07)";
const TEXT      = "#F1F7F9";
const MUTED     = "#8B8FA8";
const LAVENDER  = "#C4C1FF";

const platforms = [
  { name: "Vinted",  color: "#27AE9E" },
  { name: "Etsy",    color: "#F56400" },
  { name: "Depop",   color: "#9B59B6" },
  { name: "eBay",    color: "#E6A817" },
];

const features = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={LAVENDER} strokeWidth="1.8" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
    title: "All your sales, one place",
    desc: "Connect Vinted, Etsy, Depop and eBay. Every transaction synced automatically — no copy-pasting, no spreadsheets.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={LAVENDER} strokeWidth="1.8" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    title: "Invoices, auto-generated",
    desc: "French-compliant invoices created the moment a sale lands. VAT handled correctly for every EU country under OSS.",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={LAVENDER} strokeWidth="1.8" strokeLinecap="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: "Analytics that make sense",
    desc: "See which platform performs best, track revenue month-by-month, and know exactly what you earned.",
  },
];

function CrossedLogo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <path d="M16 2 L17.5 14.5 L30 16 L17.5 17.5 L16 30 L14.5 17.5 L2 16 L14.5 14.5 Z" fill={V} />
    </svg>
  );
}

export default function HomePage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setStatus("success");
      setName("");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <div style={{ fontFamily: "var(--font-sora, var(--font-space-grotesk, sans-serif))", background: BG, minHeight: "100vh", color: TEXT, overflowX: "hidden" }}>

      {/* ── Nav ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        borderBottom: `1px solid ${BORDER}`,
        backdropFilter: "blur(16px)",
        background: "rgba(13,14,23,0.82)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 64,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CrossedLogo size={24} />
          <span style={{
            fontFamily: "var(--font-space-grotesk, sans-serif)",
            fontWeight: 700, fontSize: 17, letterSpacing: "0.06em", color: TEXT,
          }}>
            CROSSED
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {platforms.map((p) => (
            <span key={p.name} style={{
              fontSize: 12, fontWeight: 500,
              color: p.color,
              padding: "3px 10px", borderRadius: 20,
              background: `${p.color}14`,
              border: `1px solid ${p.color}30`,
            }}>{p.name}</span>
          ))}
        </div>

        <Link href="/dashboard" style={{
          background: V, color: "#fff",
          fontFamily: "var(--font-space-grotesk, sans-serif)",
          fontWeight: 600, fontSize: 13,
          padding: "9px 20px", borderRadius: 8, textDecoration: "none",
          letterSpacing: "0.01em",
        }}>
          Log In →
        </Link>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "120px 24px 80px",
        textAlign: "center",
        position: "relative",
      }}>
        {/* Glow */}
        <div style={{
          position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)",
          width: 700, height: 500,
          background: "radial-gradient(ellipse, rgba(103,104,252,0.13) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: V_LIGHT, border: `1px solid ${V_BORDER}`,
            borderRadius: 24, padding: "6px 16px", marginBottom: 36,
          }}>
            <CrossedLogo size={12} />
            <span style={{
              fontFamily: "var(--font-space-grotesk, sans-serif)",
              fontSize: 11, fontWeight: 600, color: LAVENDER,
              letterSpacing: "0.1em", textTransform: "uppercase",
            }}>
              For independent resellers
            </span>
          </div>

          <h1 style={{
            fontFamily: "var(--font-space-grotesk, sans-serif)",
            fontSize: "clamp(44px, 7.5vw, 84px)",
            fontWeight: 700,
            lineHeight: 1.06,
            letterSpacing: "-0.03em",
            margin: "0 0 28px",
            maxWidth: 820,
          }}>
            Centralise.{" "}
            <span style={{ color: LAVENDER }}>Connect.</span>{" "}
            <span style={{ color: V }}>Control.</span>
          </h1>

          <p style={{
            fontSize: "clamp(15px, 2vw, 18px)",
            color: MUTED,
            maxWidth: 500,
            lineHeight: 1.7,
            margin: "0 auto 48px",
            fontWeight: 400,
          }}>
            One dashboard for all your resale platforms. Track every sale,
            generate compliant invoices, and understand your business.
          </p>

          {/* Platform pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 56 }}>
            {platforms.map((p) => (
              <span key={p.name} style={{
                fontSize: 13, fontWeight: 600, color: p.color,
                padding: "7px 16px", borderRadius: 8,
                background: `${p.color}14`,
                border: `1px solid ${p.color}28`,
                letterSpacing: "0.01em",
              }}>{p.name}</span>
            ))}
          </div>

          {/* Waitlist form */}
          {status === "success" ? (
            <div style={{
              background: SURFACE, border: `1px solid ${V_BORDER}`,
              borderRadius: 16, padding: "36px 44px", maxWidth: 460, width: "100%", margin: "0 auto",
            }}>
              <div style={{ fontSize: 28, marginBottom: 14, color: V }}>✦</div>
              <p style={{ fontFamily: "var(--font-space-grotesk, sans-serif)", fontWeight: 700, fontSize: 20, color: TEXT, margin: "0 0 8px" }}>
                You&apos;re on the list!
              </p>
              <p style={{ color: MUTED, fontSize: 14, margin: 0 }}>
                We&apos;ll be in touch when CROSSED is ready for you.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 460, margin: "0 auto" }}>
              <div style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: 16, padding: "28px 28px 24px",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                <p style={{
                  fontFamily: "var(--font-space-grotesk, sans-serif)",
                  fontWeight: 600, fontSize: 15, color: TEXT, margin: "0 0 6px", textAlign: "left",
                }}>
                  Join the waitlist
                </p>
                {(["Your name", "Your email"] as const).map((placeholder, i) => (
                  <input
                    key={placeholder}
                    type={i === 1 ? "email" : "text"}
                    placeholder={placeholder}
                    value={i === 0 ? name : email}
                    onChange={(e) => i === 0 ? setName(e.target.value) : setEmail(e.target.value)}
                    required
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${BORDER}`,
                      borderRadius: 8, padding: "12px 14px",
                      color: TEXT, fontSize: 14,
                      outline: "none", fontFamily: "inherit",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = V_BORDER)}
                    onBlur={(e) => (e.target.style.borderColor = BORDER)}
                  />
                ))}
                {status === "error" && (
                  <p style={{ color: "#F87171", fontSize: 13, margin: 0 }}>{errorMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{
                    background: V, color: "#fff",
                    fontFamily: "var(--font-space-grotesk, sans-serif)",
                    fontWeight: 600, fontSize: 14,
                    padding: "13px 24px", borderRadius: 8, border: "none",
                    cursor: status === "loading" ? "wait" : "pointer",
                    opacity: status === "loading" ? 0.7 : 1,
                    letterSpacing: "0.01em",
                    marginTop: 2,
                  }}
                >
                  {status === "loading" ? "Joining…" : "Get early access →"}
                </button>
                <p style={{ fontSize: 12, color: MUTED, margin: 0, opacity: 0.65 }}>
                  No spam. We&apos;ll only reach out when we&apos;re ready.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: "80px 24px 100px", maxWidth: 1080, margin: "0 auto" }}>
        <p style={{
          textAlign: "center", fontSize: 11, fontWeight: 600,
          fontFamily: "var(--font-space-grotesk, sans-serif)",
          color: LAVENDER, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14,
        }}>
          What CROSSED does
        </p>
        <h2 style={{
          fontFamily: "var(--font-space-grotesk, sans-serif)",
          textAlign: "center", fontSize: "clamp(26px, 3.5vw, 40px)",
          fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 52px", color: TEXT,
        }}>
          Built for the way you sell
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {features.map((f) => (
            <div key={f.title} style={{
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 14, padding: "28px 24px",
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: V_LIGHT, border: `1px solid ${V_BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 18,
              }}>
                {f.icon}
              </div>
              <h3 style={{
                fontFamily: "var(--font-space-grotesk, sans-serif)",
                fontWeight: 600, fontSize: 16, margin: "0 0 10px", letterSpacing: "-0.01em", color: TEXT,
              }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        maxWidth: 1032, margin: "0 auto 100px",
        background: `linear-gradient(135deg, rgba(103,104,252,0.14) 0%, rgba(103,104,252,0.04) 100%)`,
        border: `1px solid ${V_BORDER}`, borderRadius: 20,
        padding: "56px 40px", textAlign: "center",
      }}>
        <CrossedLogo size={34} />
        <h2 style={{
          fontFamily: "var(--font-space-grotesk, sans-serif)",
          fontSize: "clamp(24px, 3.5vw, 36px)",
          fontWeight: 700, letterSpacing: "-0.02em", margin: "20px 0 12px", color: TEXT,
        }}>
          Ready to take control?
        </h2>
        <p style={{ color: MUTED, fontSize: 15, margin: "0 0 32px" }}>
          Join sellers already on the waitlist.
        </p>
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          style={{
            display: "inline-block", background: V, color: "#fff",
            fontFamily: "var(--font-space-grotesk, sans-serif)",
            fontWeight: 600, fontSize: 14,
            padding: "13px 28px", borderRadius: 9, textDecoration: "none",
            letterSpacing: "0.01em",
          }}
        >
          Join the waitlist →
        </a>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        borderTop: `1px solid ${BORDER}`,
        padding: "24px 40px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CrossedLogo size={16} />
          <span style={{
            fontFamily: "var(--font-space-grotesk, sans-serif)",
            fontWeight: 700, fontSize: 13, letterSpacing: "0.06em", color: TEXT,
          }}>CROSSED</span>
        </div>
        <p style={{ fontSize: 12, color: MUTED, margin: 0, opacity: 0.6 }}>
          © {new Date().getFullYear()} CROSSED. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
